import { useEffect, useState, useMemo } from "react";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import Select from "react-select";
import { server } from "../../server";

/**
 * StaffReports
 *
 * Mirrors StudentReports / FinanceReports layout exactly:
 *  Left col  → Hero + Dataset selector + Filters + Live Snapshot
 *  Right col → Report panel (category tabs + report buttons + quick action cards)
 *
 * Datasets:
 *  1. Active Staff      – current staff directory  (StaffDirectory)
 *  2. Left Staff        – resigned / left staff    (LeftStaff)
 *  3. Attendance        – today's attendance       (StaffAttendance)
 *  4. Applied Leave     – staff leave applications (StaffAppliedLeave)
 */

// ─── helpers ──────────────────────────────────────────────────────────────────
const toUpper = (v) =>
  v !== null && v !== undefined ? String(v).toUpperCase() : "";

const today = new Date().toISOString().split("T")[0];

const writeWorkbook = (wb, filename) => {
  const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  saveAs(new Blob([buf], { type: "application/octet-stream" }), filename);
  toast.success("Report Generated");
};

const getValue = (obj, path) =>
  path.split(".").reduce((acc, key) => (acc ? acc[key] : ""), obj) || "";

// ─── dataset meta ─────────────────────────────────────────────────────────────
const DATASETS = {
  active: { label: "Active Staff", color: "success" },
  left: { label: "Left Staff", color: "danger" },
  attendance: { label: "Attendance", color: "warning" },
  leave: { label: "Applied Leave", color: "info" },
};

// ─── report categories ────────────────────────────────────────────────────────
const REPORT_CATEGORIES = [
  { key: "all", label: "All Reports" },
  { key: "directory", label: "Directory" },
  { key: "attendance", label: "Attendance" },
  { key: "leave", label: "Leave" },
  { key: "analytics", label: "Analytics" },
];

// ─────────────────────────────────────────────────────────────────────────────
function StaffReports() {
  // ── raw data ───────────────────────────────────────────────────────────────
  const [activeStaff, setActiveStaff] = useState([]);
  const [leftStaff, setLeftStaff] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [appliedLeave, setAppliedLeave] = useState([]);

  const [loadingActive, setLoadingActive] = useState(false);
  const [loadingLeft, setLoadingLeft] = useState(false);
  const [loadingAtt, setLoadingAtt] = useState(false);
  const [loadingLeave, setLoadingLeave] = useState(false);

  // ── UI state ───────────────────────────────────────────────────────────────
  const [activeDataset, setActiveDataset] = useState("active");
  const [activeCategory, setActiveCategory] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDept, setSelectedDept] = useState("");
  const [selectedGender, setSelectedGender] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // ── fetches ────────────────────────────────────────────────────────────────
  useEffect(() => {
    // Active staff
    setLoadingActive(true);
    fetch(`${server}/get_staff_data_mongodb`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: localStorage.getItem("token"),
      },
      body: JSON.stringify({}),
    })
      .then((r) => r.json())
      .then((d) => setActiveStaff(d?.["body-json"]?.body || []))
      .catch(console.error)
      .finally(() => setLoadingActive(false));

    // Left staff
    setLoadingLeft(true);
    fetch(`${server}/get_all_left_staff`, {
      headers: { Authorization: localStorage.getItem("token") },
    })
      .then((r) => r.json())
      .then((d) => setLeftStaff(d?.["body-json"]?.body?.data || []))
      .catch(console.error)
      .finally(() => setLoadingLeft(false));

    // Today's attendance
    setLoadingAtt(true);
    fetch(`${server}/staffAttendanceByDate`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: localStorage.getItem("token"),
      },
      body: JSON.stringify({ date: today }),
    })
      .then((r) => r.json())
      .then((d) => {
        let body = d?.["body-json"]?.body;
        if (typeof body === "string") {
          try {
            body = JSON.parse(body);
          } catch {
            body = {};
          }
        }
        setAttendance(Array.isArray(body?.data) ? body.data : []);
      })
      .catch(console.error)
      .finally(() => setLoadingAtt(false));

    // Applied leave
    setLoadingLeave(true);
    fetch(`${server}/GetAllAppliedStaffLeave`, {
      headers: { Authorization: localStorage.getItem("token") },
    })
      .then((r) => r.json())
      .then((d) => {
        const data = d?.staffLeaveDetails || [];
        setAppliedLeave(
          data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)),
        );
      })
      .catch(console.error)
      .finally(() => setLoadingLeave(false));
  }, []);

  // ── dataset map ────────────────────────────────────────────────────────────
  const datasetMap = {
    active: { data: activeStaff, loading: loadingActive },
    left: { data: leftStaff, loading: loadingLeft },
    attendance: { data: attendance, loading: loadingAtt },
    leave: { data: appliedLeave, loading: loadingLeave },
  };

  const currentRaw = datasetMap[activeDataset]?.data || [];
  const currentLoading = datasetMap[activeDataset]?.loading || false;

  // ── dynamic filter options ─────────────────────────────────────────────────
  const deptOptions = useMemo(() => {
    const set = new Set(
      activeStaff
        .map((s) => getValue(s, "categoryDetails.department"))
        .filter(Boolean),
    );
    return Array.from(set).map((d) => ({ value: d, label: d }));
  }, [activeStaff]);

  const genderOptions = [
    { value: "Male", label: "Male" },
    { value: "Female", label: "Female" },
  ];

  const categoryOptions = [
    { value: "Teaching", label: "Teaching" },
    { value: "Non-Teaching", label: "Non-Teaching" },
  ];

  // ── filtered data ──────────────────────────────────────────────────────────
  const filteredData = useMemo(() => {
    let data = [...currentRaw];

    if (selectedDept && activeDataset === "active") {
      data = data.filter(
        (s) => getValue(s, "categoryDetails.department") === selectedDept,
      );
    }
    if (selectedGender && activeDataset === "active") {
      data = data.filter(
        (s) => getValue(s, "basicDetails.gender") === selectedGender,
      );
    }
    if (selectedCategory && activeDataset === "active") {
      data = data.filter(
        (s) =>
          getValue(s, "categoryDetails.staffCategory") === selectedCategory,
      );
    }

    if (fromDate || toDate) {
      data = data.filter((s) => {
        const d = (s.createdAt || s.basicDetails?.doj || "")?.slice(0, 10);
        if (!d) return false;
        if (fromDate && d < fromDate) return false;
        if (toDate && d > toDate) return false;
        return true;
      });
    }

    if (searchTerm) {
      const lower = searchTerm.toLowerCase();
      const flatten = (obj) => {
        const vals = [];
        for (const k in obj) {
          const v = obj[k];
          if (v && typeof v === "object") vals.push(...flatten(v));
          else if (typeof v === "string") vals.push(v);
        }
        return vals;
      };
      data = data.filter((s) =>
        flatten(s).some((v) => v.toLowerCase().includes(lower)),
      );
    }

    return data;
  }, [
    currentRaw,
    selectedDept,
    selectedGender,
    selectedCategory,
    fromDate,
    toDate,
    searchTerm,
    activeDataset,
  ]);

  // ── live snapshot ──────────────────────────────────────────────────────────
  const snapshot = useMemo(() => {
    const total = activeStaff.length;
    const left = leftStaff.length;
    const teaching = activeStaff.filter(
      (s) => getValue(s, "categoryDetails.staffCategory") === "Teaching",
    ).length;
    const nonTeaching = total - teaching;

    const attPresent = attendance.filter(
      (s) => s?.attendance?.in?.status === "Present",
    ).length;
    const attAbsent = attendance.length - attPresent;

    const pendingLeave = appliedLeave.filter(
      (l) => l.status?.toLowerCase() === "pending",
    ).length;
    const approvedLeave = appliedLeave.filter(
      (l) => l.status?.toLowerCase() === "approved",
    ).length;

    return {
      total,
      left,
      teaching,
      nonTeaching,
      attPresent,
      attAbsent,
      pendingLeave,
      approvedLeave,
    };
  }, [activeStaff, leftStaff, attendance, appliedLeave]);

  // ── select styles ──────────────────────────────────────────────────────────
  const selectStyles = {
    control: (b) => ({
      ...b,
      border: "none",
      boxShadow: "none",
      backgroundColor: "transparent",
      minHeight: "32px",
      cursor: "pointer",
    }),
    valueContainer: (b) => ({ ...b, padding: "0" }),
    indicatorSeparator: () => ({ display: "none" }),
    dropdownIndicator: (b) => ({ ...b, padding: "4px" }),
    menu: (b) => ({
      ...b,
      borderRadius: "12px",
      overflow: "hidden",
      boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
      border: "none",
      zIndex: 5,
    }),
    option: (b, s) => ({
      ...b,
      backgroundColor: s.isSelected
        ? "#ffc107"
        : s.isFocused
          ? "#f8f9fa"
          : "white",
      color: s.isSelected ? "white" : "#212529",
      cursor: "pointer",
      fontSize: "14px",
    }),
    placeholder: (b) => ({ ...b, color: "#6c757d", fontSize: "14px" }),
    singleValue: (b) => ({ ...b, color: "#212529", fontSize: "14px" }),
  };

  // ─────────────────────────────────────────────────────────────────────────
  //  ROW FORMATTERS
  // ─────────────────────────────────────────────────────────────────────────
  const formatStaffRow = (s) => ({
    "STAFF ID": toUpper(s.staffId || ""),
    INITIALS: toUpper(getValue(s, "basicDetails.initials")),
    "FULL NAME": toUpper(getValue(s, "basicDetails.fullName")),
    GENDER: toUpper(getValue(s, "basicDetails.gender")),
    "MOBILE NO": toUpper(getValue(s, "basicDetails.mobNo")),
    "EMAIL ID": getValue(s, "basicDetails.emailId"),
    "DATE OF BIRTH": toUpper(getValue(s, "basicDetails.dob")),
    "DATE OF JOINING": toUpper(getValue(s, "basicDetails.doj")),
    "MARITAL STATUS": toUpper(getValue(s, "basicDetails.maritalStatus")),
    DESIGNATION: toUpper(getValue(s, "categoryDetails.designation")),
    DEPARTMENT: toUpper(getValue(s, "categoryDetails.department")),
    SUBJECT: toUpper(getValue(s, "categoryDetails.subject")),
    "CLASS TEACHER": toUpper(getValue(s, "categoryDetails.classTeacher")),
    SECTION: toUpper(getValue(s, "categoryDetails.section")),
    "STAFF CATEGORY": toUpper(getValue(s, "categoryDetails.staffCategory")),
    "TEACHER TYPE": toUpper(getValue(s, "categoryDetails.teacherType")),
    "BASIC QUALIFICATION": toUpper(
      getValue(s, "basicDetails.basicQualification"),
    ),
    "MASTER QUALIFICATION": toUpper(
      getValue(s, "basicDetails.masterQualification"),
    ),
    "PROF. QUALIFICATION": toUpper(
      getValue(s, "basicDetails.professionalQualification"),
    ),
    "BUS NO": toUpper(getValue(s, "transportFacility.busNumber")),
    "VEHICLE NO": toUpper(getValue(s, "transportFacility.vehicleNo")),
    "BUS STOP": toUpper(getValue(s, "transportFacility.busStop")),
  });

  const formatAttendanceRow = (s, i) => ({
    "NO.": i + 1,
    "STAFF ID": toUpper(s.staffId || ""),
    "FULL NAME": toUpper(s.name || ""),
    CATEGORY: toUpper(s.categoryDetails?.staffCategory || ""),
    DESIGNATION: toUpper(s.categoryDetails?.designation || ""),
    DEPARTMENT: toUpper(s.categoryDetails?.department || ""),
    "IN STATUS": toUpper(s.attendance?.in?.status || "Absent"),
    "IN TIME": toUpper(s.attendance?.in?.time || "—"),
    "OUT STATUS": toUpper(s.attendance?.out?.status || "Absent"),
    "OUT TIME": toUpper(s.attendance?.out?.time || "—"),
  });

  const formatLeaveRow = (l) => ({
    "LEAVE ID": toUpper(l.leaveId || l._id || ""),
    "STAFF ID": toUpper(l.staffId || ""),
    "STAFF NAME": toUpper(l.name || l.staffName || ""),
    "LEAVE TYPE": toUpper(l.leaveType || ""),
    "FROM DATE": toUpper(l.fromDate || ""),
    "TO DATE": toUpper(l.toDate || ""),
    REASON: l.reason || "",
    STATUS: toUpper(l.status || ""),
    "APPLIED DATE": l.createdAt
      ? new Date(l.createdAt).toLocaleDateString("en-GB")
      : "",
    DOCUMENT: l.document || "",
  });

  // ─────────────────────────────────────────────────────────────────────────
  //  REPORT GENERATORS — DIRECTORY
  // ─────────────────────────────────────────────────────────────────────────
  const reportAll = () => {
    if (!filteredData.length) {
      toast.error("No records to export");
      return;
    }
    const wb = XLSX.utils.book_new();
    const rows =
      activeDataset === "attendance"
        ? filteredData.map(formatAttendanceRow)
        : activeDataset === "leave"
          ? filteredData.map(formatLeaveRow)
          : filteredData.map(formatStaffRow);
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(rows),
      DATASETS[activeDataset].label.slice(0, 31),
    );
    writeWorkbook(
      wb,
      `Staff_${DATASETS[activeDataset].label.replace(/ /g, "_")}_${today}.xlsx`,
    );
  };

  const reportGroupWise =
    (groupFn, filename, formatter = formatStaffRow) =>
    () => {
      const data =
        activeDataset === "active"
          ? activeStaff
          : activeDataset === "left"
            ? leftStaff
            : filteredData;
      if (!data.length) {
        toast.error("No records to export");
        return;
      }
      const wb = XLSX.utils.book_new();
      const groups = {};
      data.forEach((s) => {
        const key = groupFn(s) || "UNKNOWN";
        if (!groups[key]) groups[key] = [];
        groups[key].push(formatter(s));
      });
      Object.entries(groups).forEach(([key, rows]) => {
        XLSX.utils.book_append_sheet(
          wb,
          XLSX.utils.json_to_sheet(rows),
          toUpper(key).slice(0, 31),
        );
      });
      writeWorkbook(wb, `${filename}_${today}.xlsx`);
    };

  const reportDepartmentWise = reportGroupWise(
    (s) => getValue(s, "categoryDetails.department"),
    "Staff_DepartmentWise",
  );

  const reportGenderWise = reportGroupWise(
    (s) => getValue(s, "basicDetails.gender"),
    "Staff_GenderWise",
  );

  const reportSubjectWise = reportGroupWise(
    (s) => getValue(s, "categoryDetails.subject"),
    "Staff_SubjectWise",
  );

  const reportQualificationWise = reportGroupWise(
    (s) => getValue(s, "basicDetails.basicQualification"),
    "Staff_QualificationWise",
  );

  const reportMaritalStatusWise = reportGroupWise(
    (s) => getValue(s, "basicDetails.maritalStatus"),
    "Staff_MaritalStatusWise",
  );

  const reportTeachingVsNonTeaching = () => {
    const data = activeStaff;
    if (!data.length) {
      toast.error("No active staff data");
      return;
    }
    const wb = XLSX.utils.book_new();
    const teaching = data.filter(
      (s) => getValue(s, "categoryDetails.staffCategory") === "Teaching",
    );
    const nonTeaching = data.filter(
      (s) => getValue(s, "categoryDetails.staffCategory") !== "Teaching",
    );
    if (teaching.length)
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(teaching.map(formatStaffRow)),
        "TEACHING",
      );
    if (nonTeaching.length)
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(nonTeaching.map(formatStaffRow)),
        "NON-TEACHING",
      );
    writeWorkbook(wb, `Staff_Teaching_NonTeaching_${today}.xlsx`);
  };

  const reportBEdWise = () => {
    const data = activeStaff;
    if (!data.length) {
      toast.error("No data");
      return;
    }
    const wb = XLSX.utils.book_new();
    const bed = data.filter((s) =>
      getValue(s, "basicDetails.professionalQualification")
        .toLowerCase()
        .includes("b.ed"),
    );
    const nonBed = data.filter(
      (s) =>
        !getValue(s, "basicDetails.professionalQualification")
          .toLowerCase()
          .includes("b.ed"),
    );
    const mini = (arr) =>
      arr.map((s) => ({
        "STAFF ID": toUpper(s.staffId),
        "FULL NAME": toUpper(
          `${getValue(s, "basicDetails.initials")}. ${getValue(s, "basicDetails.fullName")}`,
        ),
        DEPARTMENT: toUpper(getValue(s, "categoryDetails.department")),
        QUALIFICATION: toUpper(
          getValue(s, "basicDetails.professionalQualification"),
        ),
      }));
    if (bed.length)
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(mini(bed)),
        "B.Ed",
      );
    if (nonBed.length)
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(mini(nonBed)),
        "Non-B.Ed",
      );
    writeWorkbook(wb, `Staff_BEd_NonBEd_${today}.xlsx`);
  };

  const reportBirthdayWise = () => {
    const data = [...activeStaff].sort(
      (a, b) =>
        new Date(getValue(a, "basicDetails.dob")) -
        new Date(getValue(b, "basicDetails.dob")),
    );
    if (!data.length) {
      toast.error("No data");
      return;
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(data.map(formatStaffRow)),
      "BIRTHDAYS",
    );
    writeWorkbook(wb, `Staff_BirthdayWise_${today}.xlsx`);
  };

  const reportBirthdayToday = () => {
    const t = new Date();
    const md = `${String(t.getMonth() + 1).padStart(2, "0")}-${String(t.getDate()).padStart(2, "0")}`;
    const list = activeStaff.filter((s) => {
      const dob = getValue(s, "basicDetails.dob");
      if (!dob) return false;
      const parts = dob.split("-");
      return parts.length >= 3 ? `${parts[1]}-${parts[2]}` === md : false;
    });
    if (!list.length) {
      toast.error("No staff birthdays today");
      return;
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(list.map(formatStaffRow)),
      "BIRTHDAYS TODAY",
    );
    writeWorkbook(wb, `Staff_BirthdayToday_${today}.xlsx`);
  };

  const reportTransportStaff = () => {
    const list = activeStaff.filter((s) =>
      getValue(s, "transportFacility.busStop"),
    );
    if (!list.length) {
      toast.error("No transport staff found");
      return;
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(list.map(formatStaffRow)),
      "TRANSPORT STAFF",
    );
    writeWorkbook(wb, `Staff_TransportWise_${today}.xlsx`);
  };

  const reportLeftStaff = () => {
    if (!leftStaff.length) {
      toast.error("No left staff data");
      return;
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(leftStaff.map(formatStaffRow)),
      "LEFT STAFF",
    );
    writeWorkbook(wb, `Staff_Left_${today}.xlsx`);
  };

  // ─────────────────────────────────────────────────────────────────────────
  //  REPORT GENERATORS — ATTENDANCE
  // ─────────────────────────────────────────────────────────────────────────
  const reportAttendanceAll = () => {
    if (!attendance.length) {
      toast.error("No attendance data");
      return;
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(attendance.map(formatAttendanceRow)),
      "ALL ATTENDANCE",
    );
    writeWorkbook(wb, `Staff_Attendance_All_${today}.xlsx`);
  };

  const reportAttendancePresent = () => {
    const list = attendance.filter(
      (s) => s?.attendance?.in?.status === "Present",
    );
    if (!list.length) {
      toast.error("No present records");
      return;
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(list.map(formatAttendanceRow)),
      "PRESENT",
    );
    writeWorkbook(wb, `Staff_Present_${today}.xlsx`);
  };

  const reportAttendanceAbsent = () => {
    const list = attendance.filter(
      (s) => s?.attendance?.in?.status !== "Present",
    );
    if (!list.length) {
      toast.error("No absent records");
      return;
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(list.map(formatAttendanceRow)),
      "ABSENT",
    );
    writeWorkbook(wb, `Staff_Absent_${today}.xlsx`);
  };

  const reportAttendanceDeptWise = () => {
    if (!attendance.length) {
      toast.error("No attendance data");
      return;
    }
    const wb = XLSX.utils.book_new();
    const groups = {};
    attendance.forEach((s) => {
      const dept = s.categoryDetails?.department || "UNKNOWN";
      if (!groups[dept]) groups[dept] = [];
      groups[dept].push(formatAttendanceRow(s, groups[dept].length));
    });
    Object.entries(groups).forEach(([dept, rows]) => {
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(rows),
        toUpper(dept).slice(0, 31),
      );
    });
    writeWorkbook(wb, `Staff_Attendance_DeptWise_${today}.xlsx`);
  };

  const reportAttendanceCategoryWise = () => {
    if (!attendance.length) {
      toast.error("No attendance data");
      return;
    }
    const wb = XLSX.utils.book_new();
    const groups = {};
    attendance.forEach((s) => {
      const cat = s.categoryDetails?.staffCategory || "UNKNOWN";
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(formatAttendanceRow(s, groups[cat].length));
    });
    Object.entries(groups).forEach(([cat, rows]) => {
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(rows),
        toUpper(cat).slice(0, 31),
      );
    });
    writeWorkbook(wb, `Staff_Attendance_CategoryWise_${today}.xlsx`);
  };

  // ─────────────────────────────────────────────────────────────────────────
  //  REPORT GENERATORS — LEAVE
  // ─────────────────────────────────────────────────────────────────────────
  const leaveByStatus = (status, filename) => {
    const list = appliedLeave.filter((l) => l.status?.toLowerCase() === status);
    if (!list.length) {
      toast.error(`No ${status} leave records`);
      return;
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(list.map(formatLeaveRow)),
      toUpper(status),
    );
    writeWorkbook(wb, `${filename}_${today}.xlsx`);
  };

  const reportLeaveAll = () => {
    if (!appliedLeave.length) {
      toast.error("No leave data");
      return;
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(appliedLeave.map(formatLeaveRow)),
      "ALL LEAVES",
    );
    writeWorkbook(wb, `Staff_Leave_All_${today}.xlsx`);
  };
  const reportLeavePending = () =>
    leaveByStatus("pending", "Staff_Leave_Pending");
  const reportLeaveApproved = () =>
    leaveByStatus("approved", "Staff_Leave_Approved");
  const reportLeaveRejected = () =>
    leaveByStatus("rejected", "Staff_Leave_Rejected");

  const reportLeaveWithDoc = () => {
    const list = appliedLeave.filter(
      (l) => l.document && l.document.trim() !== "",
    );
    if (!list.length) {
      toast.error("No leaves with documents");
      return;
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(list.map(formatLeaveRow)),
      "WITH DOCUMENT",
    );
    writeWorkbook(wb, `Staff_Leave_WithDoc_${today}.xlsx`);
  };

  // ─────────────────────────────────────────────────────────────────────────
  //  ANALYTICS
  // ─────────────────────────────────────────────────────────────────────────
  const reportSummary = () => {
    const deptGroups = {};
    activeStaff.forEach((s) => {
      const dept = getValue(s, "categoryDetails.department") || "UNKNOWN";
      if (!deptGroups[dept])
        deptGroups[dept] = {
          Department: dept,
          Total: 0,
          Teaching: 0,
          "Non-Teaching": 0,
          Male: 0,
          Female: 0,
        };
      deptGroups[dept].Total++;
      const cat = getValue(s, "categoryDetails.staffCategory");
      const gender = getValue(s, "basicDetails.gender");
      if (cat === "Teaching") deptGroups[dept].Teaching++;
      if (cat !== "Teaching") deptGroups[dept]["Non-Teaching"]++;
      if (gender === "Male") deptGroups[dept].Male++;
      if (gender === "Female") deptGroups[dept].Female++;
    });
    const rows = Object.values(deptGroups);
    const totals = rows.reduce(
      (acc, r) => {
        acc.Total += r.Total;
        acc.Teaching += r.Teaching;
        acc["Non-Teaching"] += r["Non-Teaching"];
        acc.Male += r.Male;
        acc.Female += r.Female;
        return acc;
      },
      {
        Department: "TOTAL",
        Total: 0,
        Teaching: 0,
        "Non-Teaching": 0,
        Male: 0,
        Female: 0,
      },
    );
    rows.push(totals);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(rows),
      "Staff Summary",
    );
    writeWorkbook(wb, `Staff_Summary_${today}.xlsx`);
  };

  const reportMasterCombined = () => {
    if (!activeStaff.length && !leftStaff.length) {
      toast.error("No data");
      return;
    }
    const wb = XLSX.utils.book_new();
    const tag = (arr, status) =>
      arr.map((s) => {
        const row = formatStaffRow(s);
        row["STATUS"] = status;
        return row;
      });
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet([
        ...tag(activeStaff, "ACTIVE"),
        ...tag(leftStaff, "LEFT"),
      ]),
      "MASTER",
    );

    // Attendance summary sheet
    if (attendance.length) {
      const attRows = [
        { CATEGORY: "Total Staff", COUNT: attendance.length },
        {
          CATEGORY: "Present (IN)",
          COUNT: attendance.filter(
            (s) => s?.attendance?.in?.status === "Present",
          ).length,
        },
        {
          CATEGORY: "Absent",
          COUNT: attendance.filter(
            (s) => s?.attendance?.in?.status !== "Present",
          ).length,
        },
        {
          CATEGORY: "Present (OUT)",
          COUNT: attendance.filter(
            (s) => s?.attendance?.out?.status === "Present",
          ).length,
        },
      ];
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(attRows),
        "Attendance Summary",
      );
    }

    // Leave summary sheet
    if (appliedLeave.length) {
      const leaveRows = [
        { STATUS: "Total", COUNT: appliedLeave.length },
        {
          STATUS: "Pending",
          COUNT: appliedLeave.filter(
            (l) => l.status?.toLowerCase() === "pending",
          ).length,
        },
        {
          STATUS: "Approved",
          COUNT: appliedLeave.filter(
            (l) => l.status?.toLowerCase() === "approved",
          ).length,
        },
        {
          STATUS: "Rejected",
          COUNT: appliedLeave.filter(
            (l) => l.status?.toLowerCase() === "rejected",
          ).length,
        },
      ];
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(leaveRows),
        "Leave Summary",
      );
    }

    writeWorkbook(wb, `Staff_MasterReport_${today}.xlsx`);
  };

  const reportActiveVsLeft = () => {
    const wb = XLSX.utils.book_new();
    const rows = [
      { STATUS: "Active Staff", COUNT: activeStaff.length },
      { STATUS: "Left Staff", COUNT: leftStaff.length },
      { STATUS: "Total", COUNT: activeStaff.length + leftStaff.length },
    ];
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(rows),
      "Strength Comparison",
    );
    writeWorkbook(wb, `Staff_ActiveVsLeft_${today}.xlsx`);
  };

  // ─────────────────────────────────────────────────────────────────────────
  //  REPORT DEFINITIONS LIST
  // ─────────────────────────────────────────────────────────────────────────
  const reports = [
    // Directory
    { cat: "directory", label: "All Staff", action: reportAll },
    {
      cat: "directory",
      label: "Department Wise",
      action: reportDepartmentWise,
    },
    { cat: "directory", label: "Gender Wise", action: reportGenderWise },
    { cat: "directory", label: "Subject Wise", action: reportSubjectWise },
    {
      cat: "directory",
      label: "Qualification Wise",
      action: reportQualificationWise,
    },
    {
      cat: "directory",
      label: "Marital Status Wise",
      action: reportMaritalStatusWise,
    },
    {
      cat: "directory",
      label: "Teaching vs Non-Teaching",
      action: reportTeachingVsNonTeaching,
    },
    { cat: "directory", label: "B.Ed / Non-B.Ed", action: reportBEdWise },
    { cat: "directory", label: "Birthday Wise", action: reportBirthdayWise },
    { cat: "directory", label: "Birthdays Today", action: reportBirthdayToday },
    {
      cat: "directory",
      label: "Transport Staff",
      action: reportTransportStaff,
    },
    { cat: "directory", label: "Left Staff", action: reportLeftStaff },
    // Attendance
    { cat: "attendance", label: "All Attendance", action: reportAttendanceAll },
    {
      cat: "attendance",
      label: "Present Staff",
      action: reportAttendancePresent,
    },
    {
      cat: "attendance",
      label: "Absent Staff",
      action: reportAttendanceAbsent,
    },
    {
      cat: "attendance",
      label: "Department Wise",
      action: reportAttendanceDeptWise,
    },
    {
      cat: "attendance",
      label: "Category Wise",
      action: reportAttendanceCategoryWise,
    },
    // Leave
    { cat: "leave", label: "All Applied Leave", action: reportLeaveAll },
    { cat: "leave", label: "Pending", action: reportLeavePending },
    { cat: "leave", label: "Approved", action: reportLeaveApproved },
    { cat: "leave", label: "Rejected", action: reportLeaveRejected },
    { cat: "leave", label: "With Document", action: reportLeaveWithDoc },
    // Analytics
    { cat: "analytics", label: "Staff Summary", action: reportSummary },
    { cat: "analytics", label: "Active vs Left", action: reportActiveVsLeft },
    {
      cat: "analytics",
      label: "Master Combined Report",
      action: reportMasterCombined,
    },
  ];

  const visibleReports =
    activeCategory === "all"
      ? reports
      : reports.filter((r) => r.cat === activeCategory);

  // ─────────────────────────────────────────────────────────────────────────
  //  RENDER
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <>
      <div id="db-wrapper">
        <main id="page-content">
          <div className="container-fluid my-3 px-3">
            <div className="row g-2">
              {/* ══════════════ LEFT COLUMN ══════════════ */}
              <div className="col-lg-8">
                {/* ── HERO HEADER ── */}
                <div className="bg-white rounded-4 p-4 mb-2">
                  <div className="bg-light rounded-4 p-4 position-relative overflow-hidden">
                    <div className="d-flex align-items-center">
                      <p className="badge bg-white text-dark rounded-pill px-4 py-2 mb-0 fs-6 fw-medium">
                        <i className="bi bi-people me-1"></i> Reports &amp;
                        Analytics
                      </p>
                      <div className="ms-4">
                        <h3 className="fw-bold report-title m-0 lh-sm text-dark">
                          Staff Insights Hub
                        </h3>
                        <p className="text-secondary fs-6 fw-medium mb-0 lh-sm">
                          Generate, filter &amp; download every staff report —
                          all in one place.
                        </p>
                      </div>
                    </div>

                    {/* DATASET SELECTOR */}
                    <div className="mt-4">
                      <div className="row g-1">
                        {Object.entries(DATASETS).map(([key, ds]) => {
                          const isActive = activeDataset === key;
                          const count = datasetMap[key]?.data?.length || 0;
                          return (
                            <div className="col-md-3 col-6" key={key}>
                              <div
                                onClick={() => setActiveDataset(key)}
                                className={`p-3 rounded-4 cursor-pointer position-relative ${
                                  isActive
                                    ? `bg-${ds.color} text-white`
                                    : `bg-${ds.color}-soft`
                                }`}
                              >
                                {isActive && (
                                  <span className="position-absolute top-0 end-0 m-2">
                                    <i className="bi bi-check-circle-fill text-white fs-5"></i>
                                  </span>
                                )}
                                <p
                                  className={`m-0 fw-normal fs-5 lh-sm ${isActive ? "text-white" : "text-dark"}`}
                                >
                                  {ds.label}
                                </p>
                                <h3
                                  className={`fw-bold m-0 lh-1 mt-1 ${isActive ? "text-white" : "text-dark"}`}
                                >
                                  {count.toLocaleString("en-IN")}
                                </h3>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>

                {/* ── FILTERS ── */}
                <div className="bg-white rounded-4 p-4 mb-2">
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <p className="m-0 fw-medium lh-1 text-dark fs-5">
                      <i className="bi bi-funnel-fill me-2 text-secondary"></i>
                      Refine Results
                    </p>
                    <span
                      onClick={() => {
                        setSelectedDept("");
                        setSelectedGender("");
                        setSelectedCategory("");
                        setFromDate("");
                        setToDate("");
                        setSearchTerm("");
                      }}
                      className="badge bg-danger-soft text-dark rounded-3 px-3 py-2 cursor-pointer fw-medium"
                    >
                      <i className="bi bi-arrow-counterclockwise me-1"></i>{" "}
                      Reset
                    </span>
                  </div>

                  {/* Search */}
                  <div className="bg-light rounded-4 mb-3">
                    <form
                      className="d-flex align-items-center"
                      onSubmit={(e) => e.preventDefault()}
                    >
                      <span className="position-absolute ps-3 search-icon">
                        <i className="bi bi-filter-circle-fill ms-1" />
                      </span>
                      <input
                        type="search"
                        className="form-control ps-7 rounded-4 border-0 bg-light"
                        placeholder="Search staff ID, name, department, designation..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </form>
                  </div>

                  {/* Department, Gender & Category */}
                  <div className="bg-light rounded-4 py-3 px-4 mb-2">
                    <p className="m-0 fw-medium lh-1 text-dark mb-2">
                      Department, Gender &amp; Category
                    </p>
                    <div className="row g-2">
                      <div className="col-md-4">
                        <div className="bg-white rounded-3 px-3 py-1 d-flex align-items-center h-100">
                          <i className="bi bi-building text-secondary me-2"></i>
                          <Select
                            className="flex-grow-1"
                            isClearable
                            placeholder="All Departments"
                            value={
                              selectedDept
                                ? { value: selectedDept, label: selectedDept }
                                : null
                            }
                            onChange={(opt) =>
                              setSelectedDept(opt ? opt.value : "")
                            }
                            options={deptOptions}
                            styles={selectStyles}
                          />
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="bg-white rounded-3 px-3 py-1 d-flex align-items-center h-100">
                          <i className="bi bi-gender-ambiguous text-secondary me-2"></i>
                          <Select
                            className="flex-grow-1"
                            isClearable
                            placeholder="All Genders"
                            value={
                              selectedGender
                                ? {
                                    value: selectedGender,
                                    label: selectedGender,
                                  }
                                : null
                            }
                            onChange={(opt) =>
                              setSelectedGender(opt ? opt.value : "")
                            }
                            options={genderOptions}
                            styles={selectStyles}
                          />
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="bg-white rounded-3 px-3 py-1 d-flex align-items-center h-100">
                          <i className="bi bi-tag-fill text-secondary me-2"></i>
                          <Select
                            className="flex-grow-1"
                            isClearable
                            placeholder="All Categories"
                            value={
                              selectedCategory
                                ? {
                                    value: selectedCategory,
                                    label: selectedCategory,
                                  }
                                : null
                            }
                            onChange={(opt) =>
                              setSelectedCategory(opt ? opt.value : "")
                            }
                            options={categoryOptions}
                            styles={selectStyles}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Date Range */}
                  <div className="bg-light rounded-4 py-3 px-4 mb-2">
                    <p className="m-0 fw-medium lh-1 text-dark mb-2">
                      Date of Joining Range
                    </p>
                    <div className="row g-2">
                      <div className="col-md-6">
                        <div className="bg-white rounded-3 px-3 py-2 d-flex align-items-center">
                          <i className="bi bi-calendar-event text-secondary me-2"></i>
                          <input
                            type="date"
                            className="form-control border-0 p-0 fs-6 bg-transparent"
                            value={fromDate}
                            onChange={(e) => setFromDate(e.target.value)}
                          />
                        </div>
                        <p className="m-0 text-secondary fs-6 mt-1 ms-2 lh-1">
                          From
                        </p>
                      </div>
                      <div className="col-md-6">
                        <div className="bg-white rounded-3 px-3 py-2 d-flex align-items-center">
                          <i className="bi bi-calendar-check text-secondary me-2"></i>
                          <input
                            type="date"
                            className="form-control border-0 p-0 fs-6 bg-transparent"
                            value={toDate}
                            onChange={(e) => setToDate(e.target.value)}
                          />
                        </div>
                        <p className="m-0 text-secondary fs-6 mt-1 ms-2 lh-1">
                          To
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* ── LIVE SNAPSHOT ── */}
                <div className="bg-white rounded-4 p-4 mb-2">
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <p className="m-0 fw-medium lh-1 text-dark fs-5">
                      <i className="bi bi-pie-chart-fill me-2 text-secondary"></i>
                      Live Snapshot
                    </p>
                    <span className="badge bg-success-soft text-dark rounded-3 px-4 py-2 fw-medium">
                      <i className="bi bi-broadcast me-1"></i> Real-time
                    </span>
                  </div>

                  <div className="row g-2">
                    {/* Donut — Teaching vs Non-Teaching */}
                    <div className="col-md-5">
                      <div className="bg-danger-soft rounded-4 p-4 h-100 d-flex flex-column align-items-center justify-content-center position-relative">
                        <svg viewBox="0 0 100 100" className="w-75">
                          <circle
                            cx="50"
                            cy="50"
                            r="40"
                            fill="none"
                            stroke="#fff"
                            strokeWidth="12"
                          />
                          <circle
                            cx="50"
                            cy="50"
                            r="40"
                            fill="none"
                            stroke="#0dcaf0"
                            strokeWidth="12"
                            strokeDasharray={`${snapshot.total ? (snapshot.teaching / snapshot.total) * 251.2 : 0} 251.2`}
                            transform="rotate(-90 50 50)"
                          />
                          <circle
                            cx="50"
                            cy="50"
                            r="40"
                            fill="none"
                            stroke="#ffc107"
                            strokeWidth="12"
                            strokeDasharray={`${snapshot.total ? (snapshot.nonTeaching / snapshot.total) * 251.2 : 0} 251.2`}
                            strokeDashoffset={`${snapshot.total ? -(snapshot.teaching / snapshot.total) * 251.2 : 0}`}
                            transform="rotate(-90 50 50)"
                          />
                        </svg>
                        <div className="position-absolute text-center">
                          <p className="m-0 fs-6 text-secondary lh-1">Total</p>
                          <h2 className="m-0 fw-bold text-dark lh-1 mt-1">
                            {snapshot.total.toLocaleString("en-IN")}
                          </h2>
                        </div>
                      </div>
                    </div>

                    {/* Stats grid */}
                    <div className="col-md-7">
                      <div className="row g-1 h-100">
                        <div className="col-6">
                          <div className="bg-info-soft rounded-4 p-4 h-100">
                            <i className="bi bi-person-workspace text-dark fs-3 d-block lh-1"></i>
                            <p className="m-0 fs-6 text-secondary lh-1 mt-2">
                              Teaching
                            </p>
                            <h4 className="m-0 fw-bold text-dark">
                              {snapshot.teaching.toLocaleString("en-IN")}
                            </h4>
                            <p className="m-0 fs-6 text-secondary mt-1 lh-1">
                              {snapshot.total
                                ? Math.round(
                                    (snapshot.teaching / snapshot.total) * 100,
                                  )
                                : 0}
                              % of total
                            </p>
                          </div>
                        </div>
                        <div className="col-6">
                          <div className="bg-warning-soft rounded-4 p-4 h-100">
                            <i className="bi bi-person-gear text-dark fs-3 d-block lh-1"></i>
                            <p className="m-0 fs-6 text-secondary lh-1 mt-2">
                              Non-Teaching
                            </p>
                            <h4 className="m-0 fw-bold text-dark">
                              {snapshot.nonTeaching.toLocaleString("en-IN")}
                            </h4>
                            <p className="m-0 fs-6 text-secondary mt-1 lh-1">
                              {snapshot.total
                                ? Math.round(
                                    (snapshot.nonTeaching / snapshot.total) *
                                      100,
                                  )
                                : 0}
                              % of total
                            </p>
                          </div>
                        </div>
                        <div className="col-6">
                          <div className="bg-success-soft rounded-4 p-4 h-100">
                            <i className="bi bi-person-check-fill text-dark fs-3 d-block lh-1"></i>
                            <p className="m-0 fs-6 text-secondary lh-1 mt-2">
                              Present Today
                            </p>
                            <h4 className="m-0 fw-bold text-dark">
                              {snapshot.attPresent.toLocaleString("en-IN")}
                            </h4>
                            <p className="m-0 fs-6 text-secondary mt-1 lh-1">
                              {snapshot.attAbsent} absent
                            </p>
                          </div>
                        </div>
                        <div className="col-6">
                          <div className="bg-dark-soft rounded-4 p-4 h-100">
                            <i className="bi bi-clipboard-check text-dark fs-3 d-block lh-1"></i>
                            <p className="m-0 fs-6 text-secondary lh-1 mt-2">
                              Leave Pending
                            </p>
                            <h4 className="m-0 fw-bold text-dark">
                              {snapshot.pendingLeave.toLocaleString("en-IN")}
                            </h4>
                            <p className="m-0 fs-6 text-secondary mt-1 lh-1">
                              {snapshot.approvedLeave} approved
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* ══════════════ RIGHT COLUMN ══════════════ */}
              <div className="col-lg-4">
                <div className="mb-3">
                  <div className="mt-2 ms-4">
                    <nav aria-label="breadcrumb">
                      <ol className="breadcrumb pe-3">
                        <li className="breadcrumb-item">
                          <a href="#">Reports &amp; Analytics</a>
                        </li>
                        <li
                          className="breadcrumb-item active"
                          aria-current="page"
                        >
                          Staff
                        </li>
                      </ol>
                    </nav>
                  </div>
                </div>

                <div className="p-4 bg-white rounded-4">
                  {/* Avatar + count */}
                  <div className="bg-danger-soft rounded-4 p-3 d-flex align-items-center">
                    <div className="avatar-group">
                      {[1, 2, 3, 4, 5].map((num) => (
                        <span className="avatar avatar-md" key={num}>
                          <img
                            alt={`avatar-${num}`}
                            src={`/assets/images/students/${num}.jpg`}
                            className="rounded-circle"
                          />
                        </span>
                      ))}
                    </div>
                    <h3 className="fw-bold m-0 text-center fs-4 ms-3">
                      {filteredData.length.toLocaleString("en-IN")}
                    </h3>
                  </div>

                  {/* Title row */}
                  <div className="d-flex justify-content-between align-items-center mt-3">
                    <div className="d-flex align-items-center">
                      {currentLoading ? (
                        <div
                          className="spinner-border text-dark data-loader-spinner"
                          role="status"
                        ></div>
                      ) : (
                        <img src="/assets/images/folder.svg" alt="" />
                      )}
                      <p className="my-0 fw-semibold lh-1 fs-5 text-dark mx-3">
                        {DATASETS[activeDataset]?.label}
                        <br />
                        <span className="text-secondary fs-6 fw-medium lh-sm">
                          {reports.length} Reports Available
                        </span>
                      </p>
                    </div>
                    <span
                      onClick={reportAll}
                      className="badge bg-pink rounded-3 px-4 py-2 me-1 mt-1 cursor-pointer"
                    >
                      Export
                    </span>
                  </div>

                  {/* Category tabs */}
                  <div className="mt-3 d-flex flex-wrap gap-1">
                    {REPORT_CATEGORIES.map((cat) => (
                      <div
                        key={cat.key}
                        className={`bg-dark-soft rounded-3 cursor-pointer px-3 py-1 fs-6 ${
                          activeCategory === cat.key
                            ? "bg-warning text-white border-warning"
                            : ""
                        }`}
                        onClick={() => setActiveCategory(cat.key)}
                      >
                        {cat.label}
                      </div>
                    ))}
                  </div>

                  {/* Report buttons */}
                  <div className="px-4 py-2 mt-3 bg-light rounded-4">
                    <li className="list-group-item px-0">
                      <a
                        className="d-flex align-items-center text-inherit mb-0 justify-content-between"
                        data-bs-toggle="collapse"
                        href="#staffReportsCollapse"
                        role="button"
                        aria-expanded="true"
                        aria-controls="staffReportsCollapse"
                      >
                        <p className="m-0 fw-medium lh-sm text-dark">
                          Available Reports ({visibleReports.length})
                        </p>
                        <span className="chevron-arrow ms-4">
                          <i className="bi bi-chevron-down fs-4" />
                        </span>
                      </a>
                      <div className="collapse" id="staffReportsCollapse">
                        <div className="py-3">
                          <div className="rounded-4 d-flex flex-wrap">
                            {visibleReports.map((r, i) => (
                              <span
                                key={i}
                                onClick={r.action}
                                className="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                              >
                                {r.label}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </li>
                  </div>

                  {/* Quick action cards — Summary */}
                  <div className="mt-2">
                    <div className="row g-1">
                      <div className="col-6">
                        <div
                          onClick={reportSummary}
                          className="p-3 bg-success-soft rounded-4 cursor-pointer"
                        >
                          <p className="m-0 text-center fw-medium lh-sm text-dark">
                            Summary
                          </p>
                          <p className="text-center fs-5 m-0 lh-1">
                            Department Wise
                          </p>
                        </div>
                      </div>
                      <div className="col-6">
                        <div
                          onClick={reportActiveVsLeft}
                          className="p-3 bg-info-soft rounded-4 cursor-pointer"
                        >
                          <p className="m-0 text-center fw-medium lh-sm text-dark">
                            Comparison
                          </p>
                          <p className="text-center fs-5 m-0 lh-1">
                            Active vs Left
                          </p>
                        </div>
                      </div>
                      <div className="col-12">
                        <div
                          onClick={reportMasterCombined}
                          className="p-3 bg-danger-soft rounded-4 cursor-pointer"
                        >
                          <p className="m-0 text-center fw-medium lh-sm text-dark">
                            Master Combined Report
                          </p>
                          <p className="text-center fs-5 m-0 lh-1">
                            All Statuses in One File
                          </p>
                        </div>
                      </div>
                      <div className="col-6">
                        <div
                          onClick={reportAttendancePresent}
                          className="p-3 bg-success-soft rounded-4 cursor-pointer"
                        >
                          <p className="m-0 text-center fw-medium lh-sm text-dark">
                            Staff Attendance
                          </p>
                          <p className="text-center fs-5 m-0 lh-1">
                            Present Today
                          </p>
                        </div>
                      </div>
                      <div className="col-6">
                        <div
                          onClick={reportAttendanceAbsent}
                          className="p-3 bg-danger-soft rounded-4 cursor-pointer"
                        >
                          <p className="m-0 text-center fw-medium lh-sm text-dark">
                            Staff Attendance
                          </p>
                          <p className="text-center fs-5 m-0 lh-1">
                            Absent Today
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
}

export default StaffReports;
